import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Msg91OtpWidget, {
  isMsg91WidgetConfigured,
} from '../../components/Msg91OtpWidget';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Divider,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ConfirmationNumber as OtpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { getApiBaseUrl as getSharedApiBaseUrl } from '../../services/httpClient';

const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '');

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const localOverride = window.localStorage.getItem('REACT_APP_API_URL');
    if (localOverride) {
      return trimTrailingSlash(localOverride);
    }
  }

  // Same host-aware resolution as the rest of admin-panel (services.knls.in → /api).
  return trimTrailingSlash(getSharedApiBaseUrl());
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Shared OTP forgot-password for franchisee (/locsydashboard) and citizen user (/user-login).
 * Pass `variant="user"` or open `/user-login/forgot-password`.
 */
const ForgotPassword = ({ variant: variantProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserReset =
    variantProp === 'user' ||
    String(location.pathname || '').includes('/user-login');

  const apiBase = isUserReset
    ? `${API_BASE_URL}/auth/forgot-password`
    : `${API_BASE_URL}/franchisee/admin/forgot-password`;
  const loginPath = isUserReset ? '/user-login' : '/locsydashboard';
  const accountHint = isUserReset
    ? 'Use the mobile number linked to your Locsy user account.'
    : 'Use the number linked to your franchisee account.';

  const useMsg91Widget = isMsg91WidgetConfigured();
  const stepLabels = useMemo(
    () =>
      useMsg91Widget
        ? ['Phone number', 'New password']
        : ['Phone number', 'OTP', 'New password'],
    [useMsg91Widget],
  );
  const successStepIndex = stepLabels.length;

  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const lastPhoneForMsg91Ref = useRef('');

  const step1Validation = yup.object({
    phone: yup
      .string()
      .required('Phone number is required')
      .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
  });

  const step2Validation = useMemo(
    () =>
      yup.object({
        otp: yup
          .string()
          .required('OTP is required')
          .matches(/^\d{6}$/, 'OTP must be 6 digits'),
      }),
    [],
  );

  const phoneDigitsRef = useRef('');
  const step3Validation = useMemo(
    () =>
      yup.object({
        new_password: yup
          .string()
          .required('Password is required')
          .test(
            'password-strength',
            isUserReset
              ? 'Use a 4-digit PIN or at least 6 characters'
              : 'Password must be at least 6 characters',
            (value) => {
              const v = String(value || '');
              if (isUserReset) {
                return /^\d{4}$/.test(v) || v.length >= 6;
              }
              return v.length >= 6;
            },
          )
          .test(
            'not-last4',
            'Do not use the last 4 digits of your phone as your PIN',
            (value) => {
              if (!isUserReset) return true;
              const last4 = String(phoneDigitsRef.current || '').slice(-4);
              return !(last4 && String(value) === last4);
            },
          ),
        confirm_password: yup
          .string()
          .required('Confirm password is required')
          .oneOf([yup.ref('new_password')], 'Passwords must match'),
      }),
    [isUserReset],
  );

  const step1Formik = useFormik({
    initialValues: { phone: '' },
    validationSchema: step1Validation,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      phoneDigitsRef.current = values.phone;

      try {
        if (useMsg91Widget) {
          if (!msg91AccessToken) {
            setError(
              'Enter your mobile number, complete verification below, then tap Continue.',
            );
            return;
          }
        }

        const response = await axios.post(`${apiBase}/step1`, {
          phone: values.phone,
          ...(useMsg91Widget ? { skip_managed_otp: true } : {}),
        });

        if (response.data.success) {
          setActiveStep(1);
        } else {
          setError(response.data.message || 'Something went wrong. Please try again.');
        }
      } catch (err) {
        setError(
          err.response?.data?.message || 'An error occurred. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const step2Formik = useFormik({
    initialValues: { otp: '' },
    validationSchema: step2Validation,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const response = await axios.post(`${apiBase}/verify-otp`, {
          phone: step1Formik.values.phone,
          otp: values.otp,
        });
        if (!response.data?.success) {
          setError(response.data?.message || 'Invalid OTP');
          return;
        }
        setActiveStep(2);
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired OTP');
      } finally {
        setLoading(false);
      }
    },
  });

  const step3Formik = useFormik({
    initialValues: { new_password: '', confirm_password: '' },
    validationSchema: step3Validation,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const payload = {
          phone: step1Formik.values.phone,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        };
        if (useMsg91Widget) {
          payload.msg91_access_token = msg91AccessToken;
          if (msg91RequestUserId) {
            payload.msg91_request_user_id = msg91RequestUserId;
          }
        } else {
          payload.otp = step2Formik.values.otp;
        }

        const response = await axios.post(`${apiBase}/step2`, payload);

        if (response.data.success) {
          setActiveStep(successStepIndex);
          setTimeout(() => {
            navigate(loginPath);
          }, 3000);
        } else {
          setError(response.data.message || 'Failed to reset password');
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            'Failed to reset password. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!useMsg91Widget) return;
    const phone = step1Formik.values.phone || '';
    if (lastPhoneForMsg91Ref.current !== phone) {
      lastPhoneForMsg91Ref.current = phone;
      setMsg91AccessToken('');
      setMsg91RequestUserId('');
    }
  }, [step1Formik.values.phone, useMsg91Widget]);

  const handleBack = () => {
    if (activeStep <= 0) return;
    setError('');
    if (useMsg91Widget) {
      if (activeStep === 1) {
        step3Formik.resetForm();
      }
    } else {
      if (activeStep === 1) {
        step2Formik.resetForm({ values: { otp: '' } });
      }
      if (activeStep === 2) {
        step3Formik.resetForm();
      }
    }
    setActiveStep(activeStep - 1);
  };

  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Password reset successful
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You can sign in with your new password. Redirecting to the login page…
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate(loginPath)}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
          },
        }}
      >
        Go to login
      </Button>
    </Box>
  );

  const renderPhoneStep = () => (
    <Box component="form" onSubmit={step1Formik.handleSubmit}>
      <TextField
        fullWidth
        label="Registered mobile number"
        name="phone"
        value={step1Formik.values.phone}
        onChange={step1Formik.handleChange}
        onBlur={step1Formik.handleBlur}
        error={step1Formik.touched.phone && Boolean(step1Formik.errors.phone)}
        helperText={
          (step1Formik.touched.phone && step1Formik.errors.phone) ||
          (useMsg91Widget
            ? `${accountHint} You will verify it below.`
            : `${accountHint} We will send a 6-digit OTP if an account exists.`)
        }
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon color="action" />
            </InputAdornment>
          ),
        }}
        inputProps={{
          maxLength: 10,
          inputMode: 'numeric',
          type: 'tel',
          pattern: '[0-9]*',
          autoComplete: 'tel-national',
        }}
      />

      {useMsg91Widget ? (
        <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch', mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Verify mobile
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            Request the code, enter it in the widget, then tap Continue once it shows verified.
          </Typography>
          <Msg91OtpWidget
            phoneDigits10={step1Formik.values.phone}
            disabled={loading}
            onVerified={(token, meta) => {
              setMsg91AccessToken(token);
              const sessionId =
                meta && String(meta.requestUserId || meta.reqId || '').trim();
              setMsg91RequestUserId(sessionId || '');
              setError('');
            }}
            onError={(msg) => {
              setMsg91AccessToken('');
              setMsg91RequestUserId('');
              setError(msg || 'Verification failed');
            }}
          />
        </Stack>
      ) : null}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{
          mt: 1,
          py: 1.5,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
          },
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
      </Button>
    </Box>
  );

  const renderOtpStep = () => (
    <Box component="form" onSubmit={step2Formik.handleSubmit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the 6-digit code sent to {step1Formik.values.phone}.
      </Typography>
      <TextField
        fullWidth
        label="OTP"
        name="otp"
        value={step2Formik.values.otp}
        onChange={step2Formik.handleChange}
        onBlur={step2Formik.handleBlur}
        error={step2Formik.touched.otp && Boolean(step2Formik.errors.otp)}
        helperText={step2Formik.touched.otp && step2Formik.errors.otp}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <OtpIcon color="action" />
            </InputAdornment>
          ),
        }}
        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={handleBack} disabled={loading}>
          Back
        </Button>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
        </Button>
      </Box>
    </Box>
  );

  const renderPasswordStep = () => (
    <Box component="form" onSubmit={step3Formik.handleSubmit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isUserReset
          ? 'Choose a new PIN (4 digits) or password (6+ characters).'
          : 'Choose a new password for your franchisee dashboard.'}
      </Typography>
      <TextField
        fullWidth
        label={isUserReset ? 'New PIN / password' : 'New password'}
        name="new_password"
        type={showPassword ? 'text' : 'password'}
        value={step3Formik.values.new_password}
        onChange={step3Formik.handleChange}
        onBlur={step3Formik.handleBlur}
        error={
          step3Formik.touched.new_password && Boolean(step3Formik.errors.new_password)
        }
        helperText={step3Formik.touched.new_password && step3Formik.errors.new_password}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label="toggle password">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        label={isUserReset ? 'Confirm new PIN / password' : 'Confirm new password'}
        name="confirm_password"
        type={showConfirmPassword ? 'text' : 'password'}
        value={step3Formik.values.confirm_password}
        onChange={step3Formik.handleChange}
        onBlur={step3Formik.handleBlur}
        error={
          step3Formik.touched.confirm_password &&
          Boolean(step3Formik.errors.confirm_password)
        }
        helperText={
          step3Formik.touched.confirm_password && step3Formik.errors.confirm_password
        }
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
                aria-label="toggle confirm password"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={handleBack} disabled={loading}>
          Back
        </Button>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset password'}
        </Button>
      </Box>
    </Box>
  );

  const renderStepContent = () => {
    if (activeStep === successStepIndex) {
      return renderSuccess();
    }
    if (activeStep === 0) {
      return renderPhoneStep();
    }
    if (useMsg91Widget && activeStep === 1) {
      return renderPasswordStep();
    }
    if (!useMsg91Widget && activeStep === 1) {
      return renderOtpStep();
    }
    if (!useMsg91Widget && activeStep === 2) {
      return renderPasswordStep();
    }
    return null;
  };

  const stepperActiveStep =
    activeStep >= successStepIndex ? stepLabels.length : activeStep;

  const subtitle = useMsg91Widget
    ? 'Confirm your registered mobile, then set a new password.'
    : 'We will text you a one-time code, then you can set a new password.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Forgot password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
              {subtitle}
            </Typography>
          </Box>

          <Stepper activeStep={stepperActiveStep} sx={{ mb: 3 }}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          {activeStep < successStepIndex && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Remember your password?{' '}
                <Link
                  to={loginPath}
                  style={{ textDecoration: 'none', color: '#667eea', fontWeight: 600 }}
                >
                  Back to login
                </Link>
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
